import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { SesionContext, type SesionContextValue } from './sesionContexto';
import { puerto as puertoPorOmision } from './elPuerto';
import type { PuertoDeIdentidad, Sesion } from './puerto';

interface Props {
  children: ReactNode;
  /**
   * Sustituye el puerto montado. **Sólo para pruebas.**
   *
   * Es lo que permite que una prueba entre con sesión sin tocar
   * `localStorage`, y lo que evita que las pruebas de pantalla conozcan a
   * `AdaptadorLocal` —lo cual, además de feo, pondría roja la prueba que impide
   * justo eso.
   */
  puerto?: PuertoDeIdentidad;
}

export function SesionProvider({ children, puerto = puertoPorOmision }: Props) {
  const [sesion, setSesion] = useState<Sesion | null>(null);
  const [rehidratando, setRehidratando] = useState(true);

  // Leer la sesión guardada al montar. Mientras dura, `rehidratando` es cierto
  // y el guardia espera en vez de decidir: ver `sesionContexto.ts`.
  useEffect(() => {
    let vivo = true;
    puerto
      .sesionActual()
      .then((encontrada) => {
        if (vivo) setSesion(encontrada);
      })
      .catch(() => {
        // Una sesión que no se puede leer es una sesión que no hay. No se
        // enseña error: nadie pidió nada todavía.
        if (vivo) setSesion(null);
      })
      .finally(() => {
        if (vivo) setRehidratando(false);
      });
    return () => {
      vivo = false;
    };
  }, [puerto]);

  const iniciarSesion = useCallback(
    async (correo: string, contrasena: string) => {
      // El error sube tal cual: es un `ErrorDeAcceso` con su código, y quien
      // llama —el formulario— es quien sabe dónde enseñarlo.
      const nueva = await puerto.iniciarSesion(correo, contrasena);
      setSesion(nueva);
    },
    [puerto],
  );

  // Sin `setSesion`: recuperar la contraseña **no** inicia sesión. Quien acaba
  // de cambiarla va a la puerta y entra con ella, que es lo que espera y lo que
  // hace que un código de correo no sea, de hecho, una sesión.
  const pedirCodigo = useCallback(
    async (correo: string) => {
      await puerto.pedirCodigo(correo);
    },
    [puerto],
  );

  const restablecer = useCallback(
    async (correo: string, codigo: string, contrasena: string) => {
      await puerto.restablecer(correo, codigo, contrasena);
    },
    [puerto],
  );

  const cerrarSesion = useCallback(async () => {
    await puerto.cerrarSesion();
    setSesion(null);
  }, [puerto]);

  const valor = useMemo<SesionContextValue>(
    () => ({
      sesion,
      usuario: sesion?.usuario ?? null,
      rehidratando,
      esDeMentira: puerto.esDeMentira,
      iniciarSesion,
      cerrarSesion,
      pedirCodigo,
      restablecer,
    }),
    [sesion, rehidratando, puerto, iniciarSesion, cerrarSesion, pedirCodigo, restablecer],
  );

  return <SesionContext.Provider value={valor}>{children}</SesionContext.Provider>;
}
